import { EXPERIENCE_PRIMARY_FILTER, PROPERTY_TIERS, SHARE_SCOPE, TAB_STATUSES, VB_MODE } from "@/constants/index.js";

import { VisualBuilderKeys } from "./VisualBuilderKeys.js";

/**
 * Determine the visualization mode and key based on provided parameters
 * @param {Object} params - Object containing route and state parameters
 * @returns {Object} Object containing key and mode for the visualization
 */
export function getVisualBuilderMode(params) {
  const { propertyId, experienceId, userId, tabStatus, shareId } = params;
  const mediaItemId = params?.mediaItemId ?? params?.mediaId;
  const values = {};

  // Handle homebase and collect tabs
  if (propertyId && (tabStatus === TAB_STATUSES.HOMEBASE || tabStatus === TAB_STATUSES.COLLECT)) {
    values.key = VisualBuilderKeys.presentation(propertyId);
    values.mode = VB_MODE.PRESENTATION;
  }

  // Handle customize and share tabs with user context
  if (
    propertyId &&
    experienceId &&
    userId &&
    (tabStatus === TAB_STATUSES.CUSTOMIZE || tabStatus === TAB_STATUSES.SHARE)
  ) {
    values.key = VisualBuilderKeys.visualization(propertyId, experienceId, userId);
    values.mode = VB_MODE.VISUALIZATION;
  }

  // Handle public tab
  if (propertyId && experienceId && tabStatus === TAB_STATUSES.PUBLIC) {
    values.key = VisualBuilderKeys.publicVisualization(propertyId, experienceId);
    values.mode = VB_MODE.PUBLIC_VISUALIZATION;
  }

  // Handle public single share tab
  if (propertyId && mediaItemId && tabStatus === TAB_STATUSES.PUBLIC_SINGLE_SHARE) {
    values.key = VisualBuilderKeys.publicSingleShareVisualization(propertyId, mediaItemId);
    values.mode = VB_MODE.PUBLIC_SINGLE_SHARE_VISUALIZATION;
  }

  // Handle public media share tab and all assets share
  if (propertyId && tabStatus === TAB_STATUSES.PUBLIC_MEDIA_SHARE) {
    if (shareId) {
      values.key = VisualBuilderKeys.publicMediaShareVisualization(propertyId, shareId);
    }
    values.mode = VB_MODE.PUBLIC_MEDIA_SHARE_VISUALIZATION;
  }

  return values;
}

/**
 * Transform content types data to establish relationships and ordering
 * @param {Object} payload - Current state payload
 * @returns {Object} Updated payload with transformed content types data
 */
export function transformContentTypesData(payload) {
  const { contentTypes = [], contentTypesOrdered = {}, propertyId } = payload;

  // Initialize relationship containers
  const contentTypeAssetCategories = {};
  const contentTypeTags = {};

  // Add "Other" content type if it doesn't exist
  if (!contentTypes.find((contentType) => contentType.id === -1)) {
    contentTypes.push({
      id: -1,
      propertyId,
      name: "Other",
      createdAt: "",
      tags: [],
      value: "",
    });
  }

  // Process each content type
  contentTypes.forEach((contentType) => {
    const contentTypeId = `${contentType.id}`;

    // Set or preserve order
    const orderedContentType = contentTypesOrdered[contentType.id] || contentType.order;
    if (orderedContentType) {
      contentTypesOrdered[contentTypeId] = orderedContentType;
    }

    // Initialize relationships arrays
    contentTypeTags[contentTypeId] = [];
    contentTypeAssetCategories[contentTypeId] = [];
  });

  return {
    contentTypeAssetCategories,
    contentTypeTags,
    contentTypesOrdered,
  };
}

/**
 * Transform tags data to establish relationships and ordering
 * @param {Object} payload - Current state payload
 * @returns {Object} Updated payload with transformed tags data
 */
export function transformTagsData(payload) {
  const {
    hiddenTags: payloadHiddenTags = {},
    assetCategoriesOrdered: payloadAssetCategoriesOrdered = {},
    assetCategories = [],
    contentTypes = [],
    tags = [],
    tagsOrdered = {},
  } = payload;

  // Initialize relationship containers
  const tagContentTypes = {};
  const assetCategoryContentTypes = {};

  const hiddenTags = { ...payloadHiddenTags };

  // Process each tag
  tags.forEach((tag) => {
    tagContentTypes[tag.id] = [];
    hiddenTags[`media/${tag.id}`] = tag.hidden;
    tagsOrdered[tag.id] = payload.tagsOrdered?.[tag.id] || tag.order;
  });

  // Add "Other" asset category if it doesn't exist
  const nonOtherContentTypeIds = contentTypes.map((contentType) => contentType.id).filter((id) => id !== -1);

  if (!assetCategories.find((assetCategory) => assetCategory.id === -1)) {
    assetCategories.push({
      id: -1,
      name: "Other",
      createdAt: "",
      assetAmount: 0,
      contentTypes: [...nonOtherContentTypeIds],
      members: [],
      tags: [],
    });
  }

  const assetCategoriesOrdered = { ...payloadAssetCategoriesOrdered };

  // Process each asset category
  assetCategories.forEach((category) => {
    assetCategoryContentTypes[category.id] = [];
    assetCategoriesOrdered[category.id] = payload.assetCategoriesOrdered?.[category.id] || category.order || 0;
  });

  return {
    hiddenTags,
    tagsOrdered,
    tagContentTypes,
    assetCategoryContentTypes,
    assetCategoriesOrdered,
  };
}

/**
 * Transform media item data to establish relationships and handle special cases
 * @param {Object} payload - Current state payload
 * @returns {Object} Updated payload with transformed media item data
 */
export function transformMediaItemData(payload) {
  const {
    mediaItems = [],
    contentTypes: payloadContentTypes = [],
    tags: payloadTags = [],
    contentTypeTags: payloadContentTypeTags = {},
    tagContentTypes: payloadTagContentTypes = {},
    assetCategories: payloadAssetCategories = [],
    assetCategoryContentTypes: payloadAssetCategoryContentTypes = {},
    contentTypeAssetCategories: payloadContentTypeAssetCategories = {},
    hiddenTags: payloadHiddenTags = {},
    hiddenContentTypes: payloadHiddenContentTypes = {},
    hiddenAssetCategories: payloadHiddenAssetCategories = {},
    otherTag,
    tabStatus,
    propertyId,
    // primaryFilter = EXPERIENCE_PRIMARY_FILTER.TYPE,
  } = payload;

  const contentTypeTags = { ...payloadContentTypeTags };
  const tagContentTypes = { ...payloadTagContentTypes };
  const contentTypeAssetCategories = { ...payloadContentTypeAssetCategories };

  let payloadMediaItems;

  // Ensure mediaItems is an array
  if (!Array.isArray(mediaItems)) {
    payloadMediaItems = [mediaItems];
  } else {
    payloadMediaItems = mediaItems;
  }

  const assetCategoryContentTypes = {
    ...payloadAssetCategoryContentTypes,
  };

  const assetCategoryHiddenMediaItems = [];
  const contentTypeHiddenMediaItems = [];
  const acAssetCategoryHiddenMediaItems = [];
  const acContentTypeHiddenMediaItems = [];
  const ctAssetCategoryHiddenMediaItems = [];
  const ctContentTypeHiddenMediaItems = [];

  const newMediaItems = [];
  const acMediaItems = [];
  const ctMediaItems = [];

  // Process each media item
  payloadMediaItems.forEach((mediaItem) => {
    mediaItem.tags = [];

    // Handle asset category relationships
    if (payloadAssetCategoryContentTypes?.[mediaItem.assetCategoryId || -1]) {
      assetCategoryContentTypes[mediaItem.assetCategoryId || -1].push(mediaItem.contentTypeId);
    }

    if (!mediaItem.mediaItemTags) {
      mediaItem.mediaItemTags = [];
    }

    // Handle media items with no tags
    if (!mediaItem.mediaItemTags.length && otherTag) {
      mediaItem.mediaItemTags.push({
        ...otherTag,
        tagId: otherTag.id,
        mediaItemId: mediaItem.id,
      });
    }

    // Process media item tags
    if (mediaItem.mediaItemTags.length) {
      mediaItem.mediaItemTags.forEach((mediaItemTag) => {
        if (payloadTagContentTypes[mediaItemTag.id]) {
          tagContentTypes[mediaItemTag.id].push(mediaItem.contentTypeId);
        }

        mediaItem.tags.push(mediaItemTag.id);
      });

      // Handle hidden tags
      const isAllTagsHidden = mediaItem.mediaItemTags.every((item) =>
        isPublicPage(tabStatus) ? item?.hidden || !!payloadHiddenTags[item.id] : item?.hidden,
      );

      if (isAllTagsHidden && otherTag) {
        mediaItem.tags.push(otherTag.id);
        mediaItem.mediaItemTags.push({
          ...otherTag,
          tagId: otherTag.id,
          mediaItemId: mediaItem.id,
        });
      }
    }

    // Update content type relationships
    contentTypeTags[mediaItem.contentTypeId] = payloadContentTypeTags[mediaItem.contentTypeId]?.concat(mediaItem.tags);

    if (payloadContentTypeAssetCategories?.[mediaItem.contentTypeId]) {
      contentTypeAssetCategories[mediaItem.contentTypeId] = payloadContentTypeAssetCategories[
        mediaItem.contentTypeId
      ].concat(mediaItem.assetCategoryId || -1);
    }

    if (!mediaItem.assetCategoryId) {
      mediaItem.assetCategoryId = -1;
    }

    if (!mediaItem.contentTypeId) {
      const otherContentType = {
        id: -1,
        propertyId,
        name: "Other",
        createdAt: "",
        tags: [],
        value: "Other",
      };

      mediaItem.contentTypeId = -1;
      mediaItem.contentType = otherContentType;
    }

    if (isPublicPage(tabStatus) && payloadHiddenAssetCategories?.[mediaItem.assetCategoryId]) {
      const hiddenMediaItem = {
        ...mediaItem,
        assetCategoryId: -1,
      };

      newMediaItems.push(hiddenMediaItem);
      assetCategoryHiddenMediaItems.push(hiddenMediaItem);
    } else if (isPublicPage(tabStatus) && payloadHiddenContentTypes?.[mediaItem.contentTypeId]) {
      const hiddenMediaItem = {
        ...mediaItem,
        contentTypeId: -1,
      };

      newMediaItems.push(hiddenMediaItem);
      contentTypeHiddenMediaItems.push(hiddenMediaItem);
    } else {
      newMediaItems.push(mediaItem);
    }

    if (isPublicPage(tabStatus) && payloadHiddenAssetCategories?.[mediaItem.assetCategoryId]) {
      const hiddenMediaItem = {
        ...mediaItem,
        assetCategoryId: -1,
      };

      acMediaItems.push(hiddenMediaItem);
      acAssetCategoryHiddenMediaItems.push(hiddenMediaItem);
    } else {
      acMediaItems.push(mediaItem);
    }

    if (isPublicPage(tabStatus) && payloadHiddenContentTypes?.[mediaItem.contentTypeId]) {
      const hiddenMediaItem = {
        ...mediaItem,
        contentTypeId: -1,
      };

      ctMediaItems.push(hiddenMediaItem);
      ctContentTypeHiddenMediaItems.push(hiddenMediaItem);
    } else {
      ctMediaItems.push(mediaItem);
    }
  });

  // Deduplicate relationships
  Object.keys(payloadContentTypeTags).forEach(
    (typeId) => (contentTypeTags[typeId] = [...new Set(payloadContentTypeTags[typeId])]),
  );
  Object.keys(payloadTagContentTypes).forEach(
    (tagId) => (tagContentTypes[tagId] = [...new Set(payloadTagContentTypes[tagId])]),
  );

  Object.keys(payloadContentTypeAssetCategories || {}).forEach(
    (typeId) => (contentTypeAssetCategories[typeId] = [...new Set(payloadContentTypeAssetCategories[typeId])]),
  );
  Object.keys(payloadAssetCategoryContentTypes).forEach(
    (catId) => (assetCategoryContentTypes[catId] = [-1, ...new Set(payloadAssetCategoryContentTypes[catId])]),
  );

  // Update content types with relationships
  const contentTypes = payloadContentTypes.map((contentType) => {
    contentType.tags = payloadContentTypeTags[contentType.id];
    contentType.assetCategories = payloadContentTypeAssetCategories?.[contentType.id];
    return contentType;
  });

  // Update tags with relationships
  const tags = payloadTags.map((tag) => {
    tag.contentTypes = payloadTagContentTypes[tag.id];
    return tag;
  });

  let assetCategories = [...payloadAssetCategories];

  // Update asset categories with relationships
  if (payloadAssetCategories) {
    assetCategories = payloadAssetCategories.map((category) => {
      category.contentTypes = assetCategoryContentTypes[category.id];
      return category;
    });
  }

  const result = {
    mediaItems: newMediaItems,
    contentTypes,
    tags,
    assetCategories,
    contentTypeTags,
    tagContentTypes,
    contentTypeAssetCategories,
    assetCategoryContentTypes,
    assetCategoryHiddenMediaItems,
    contentTypeHiddenMediaItems,
    assetCategoryTabData: {
      assetCategoryHiddenMediaItems: acAssetCategoryHiddenMediaItems,
      contentTypeHiddenMediaItems: acContentTypeHiddenMediaItems,
      mediaItems: acMediaItems,
    },
    contentTypeTabData: {
      assetCategoryHiddenMediaItems: ctAssetCategoryHiddenMediaItems,
      contentTypeHiddenMediaItems: ctContentTypeHiddenMediaItems,
      mediaItems: ctMediaItems,
    },
  };

  return result;
}

/**
 * Filter media items based on current filters and search criteria
 * @param {Object} payload - Current state payload
 * @returns {Object} Updated payload with filtered media items
 */
export function getVisualBuilderFilteredMediaItems(payload) {
  const {
    mediaItems: payloadFilteredMediaItems = [],
    assetCategoryHiddenMediaItems: payloadAssetCategoryHiddenMediaItems = [],
    contentTypeHiddenMediaItems: payloadContentTypeHiddenMediaItems = [],
    typeFilterId,
    assetCategoryFilterId,
    search = {},
  } = payload;
  const { term = "", searchText = "", contentTypeIds = [], tagIds = [] } = search;

  // Initialize filtered collections
  const validContentTypes = {};
  const validTags = {};
  const tagMediaItems = {
    ...(payload.otherTag ? { [payload.otherTag.id]: [] } : {}),
  };
  const filteredTagMediaItems = {
    ...(payload.otherTag ? { [payload.otherTag.id]: [] } : {}),
  };
  const validAssetCategories = {};

  const assetCategoryMediaItems = { [-1]: [...payloadAssetCategoryHiddenMediaItems] };
  const filteredAssetCategoryMediaItems = { [-1]: [...payloadAssetCategoryHiddenMediaItems] };

  const contentTypeMediaItems = { [-1]: [...payloadContentTypeHiddenMediaItems] };
  const filteredContentTypeMediaItems = { [-1]: [...payloadContentTypeHiddenMediaItems] };

  // Build relationship collections
  payloadFilteredMediaItems.forEach((mediaItem) => {
    // Handle content type relationships
    if (!contentTypeMediaItems[mediaItem.contentTypeId]) {
      contentTypeMediaItems[mediaItem.contentTypeId] = [mediaItem];
    } else {
      contentTypeMediaItems[mediaItem.contentTypeId].push(mediaItem);
    }

    // Handle tag relationships
    mediaItem.tags.forEach((tagId) => {
      if (!tagMediaItems[tagId]) {
        tagMediaItems[tagId] = [{ ...mediaItem, tagId }];
      } else {
        tagMediaItems[tagId].push({ ...mediaItem, tagId });
      }
    });

    // Handle asset category relationships
    if (assetCategoryMediaItems) {
      if (assetCategoryMediaItems[mediaItem.assetCategoryId || -1]) {
        assetCategoryMediaItems[mediaItem.assetCategoryId || -1].push({
          ...mediaItem,
          assetCategoryId: mediaItem.assetCategoryId || -1,
        });
      } else {
        assetCategoryMediaItems[mediaItem.assetCategoryId || -1] = [
          { ...mediaItem, assetCategoryId: mediaItem.assetCategoryId || -1 },
        ];
      }
    }
  });

  let filteredMediaItems = [...payloadFilteredMediaItems];

  // Apply content type and tag filters
  if (contentTypeIds.length || tagIds.length) {
    filteredMediaItems = filteredMediaItems.filter((mediaItem) => {
      if (search.searchAndOr) {
        return (
          contentTypeIds.some((contentTypeId) => mediaItem.contentTypeId === contentTypeId) ||
          tagIds.some((tagId) => mediaItem.tags.includes(tagId))
        );
      }

      return (
        contentTypeIds.some((contentTypeId) => mediaItem.contentTypeId === contentTypeId) &&
        tagIds.every((tagId) => mediaItem.tags.includes(tagId))
      );
    });
  }

  // Apply search term filter
  if (term.length) {
    filteredMediaItems = filteredMediaItems.filter((mediaItem) => {
      if (!mediaItem.name) return false;
      return mediaItem.name.toLowerCase().trim().indexOf(term.toLowerCase().trim()) > -1;
    });
  }

  // Apply search text filter
  if (searchText.length) {
    filteredMediaItems = filteredMediaItems.filter((mediaItem) => {
      if (!mediaItem.name) return false;
      return mediaItem.name.toLowerCase().includes(searchText.toLowerCase().trim());
    });
  }

  // Build filtered relationships
  filteredMediaItems.forEach((mediaItem) => {
    // Handle filtered content type relationships
    if (!filteredContentTypeMediaItems[mediaItem.contentTypeId]) {
      filteredContentTypeMediaItems[mediaItem.contentTypeId] = [mediaItem];
    } else {
      if (!filteredContentTypeMediaItems[mediaItem.contentTypeId].find((item) => item.id === mediaItem.id)) {
        filteredContentTypeMediaItems[mediaItem.contentTypeId].push(mediaItem);
      }
    }

    // Skip if primary filter doesn't match
    if (
      payload.primaryFilter === EXPERIENCE_PRIMARY_FILTER.TYPE &&
      payload.typeFilterId &&
      mediaItem.contentTypeId !== payload.typeFilterId
    ) {
      return;
    }

    // Handle filtered tag relationships
    if (mediaItem.tags.length) {
      mediaItem.tags.forEach((tagId) => {
        validTags[tagId] = true;

        if (!filteredTagMediaItems[tagId]) {
          filteredTagMediaItems[tagId] = [{ ...mediaItem, tagId }];
        } else {
          filteredTagMediaItems[tagId].push({ ...mediaItem, tagId });
        }
      });
    } else {
      if (payload.otherTag) {
        filteredTagMediaItems[payload.otherTag.id].push({ ...mediaItem, tagId: payload.otherTag.id });
      }
    }

    // Handle filtered asset category relationships
    if (filteredAssetCategoryMediaItems) {
      if (filteredAssetCategoryMediaItems[mediaItem.assetCategoryId || -1]) {
        if (
          !filteredAssetCategoryMediaItems[mediaItem.assetCategoryId || -1].find((item) => item.id === mediaItem.id)
        ) {
          filteredAssetCategoryMediaItems[mediaItem.assetCategoryId || -1].push({
            ...mediaItem,
            assetCategoryId: mediaItem.assetCategoryId || -1,
          });
        }
      } else {
        filteredAssetCategoryMediaItems[mediaItem.assetCategoryId || -1] = [
          { ...mediaItem, assetCategoryId: mediaItem.assetCategoryId || -1 },
        ];
      }
    }
  });

  // Apply type filter
  if (typeFilterId) {
    filteredMediaItems = filteredMediaItems.filter((mediaItem) => mediaItem.contentTypeId === typeFilterId);
  }

  if (
    assetCategoryFilterId !== undefined &&
    assetCategoryFilterId !== null &&
    !!filteredAssetCategoryMediaItems[assetCategoryFilterId]?.length
  ) {
    filteredMediaItems = filteredMediaItems.filter(
      (mediaItem) => (mediaItem.assetCategoryId || -1) === (assetCategoryFilterId || -1),
    );
  }

  // Reset asset category filter if no items match
  if (
    assetCategoryFilterId !== undefined &&
    assetCategoryFilterId !== null &&
    !filteredAssetCategoryMediaItems[assetCategoryFilterId]?.length &&
    filteredMediaItems.length
  ) {
    payload.assetCategoryFilterId = filteredMediaItems[0].assetCategoryId || -1;
  }

  // Update valid content types
  filteredMediaItems.forEach((mediaItem) => {
    validContentTypes[mediaItem.contentTypeId] = true;
  });

  if (payload.propertyMenuItems) {
    // Filter property menu items by typeFilterId
    const filteredCurrentPropertyMenuItems = payload.propertyMenuItems.filter(
      (propertyMenuItem) => propertyMenuItem.parentId === typeFilterId,
    );
    // Sort property menu items by asc order
    const sortedFilteredCurrentPropertyMenuItems = filteredCurrentPropertyMenuItems.sort(
      (propertyMenuItemA, propertyMenuItemB) => propertyMenuItemA.order - propertyMenuItemB.order,
    );
    // Map that represents the itemId with its respective order number
    const filteredMediaItemsOrderMap = new Map(
      sortedFilteredCurrentPropertyMenuItems.map(({ itemId, order }) => [itemId, order]),
    );
    // Ensure the filteredMediaItems are in correct order by utilizing filteredMediaItemsOrderMap
    filteredMediaItems = filteredMediaItems.sort((filteredMediaItemA, filteredMediaItemB) => {
      const orderA = filteredMediaItemsOrderMap.get(filteredMediaItemA.id);
      const orderB = filteredMediaItemsOrderMap.get(filteredMediaItemB.id);

      return (orderA ?? 0) - (orderB ?? 0);
    });
  }

  let currentMediaItemId = payload.currentMediaItemId;

  // Reset current media item if it's no longer in filtered items
  if (filteredMediaItems.length && !filteredMediaItems.find((mediaItem) => mediaItem.id === currentMediaItemId)) {
    currentMediaItemId = filteredMediaItems[0].id;
  }

  // Track if search was performed
  const searchPerformed = !!contentTypeIds.length || !!tagIds.length || !!term.length || !!searchText.length;

  return {
    filteredMediaItems,
    validContentTypes,
    validTags,
    contentTypeMediaItems,
    filteredContentTypeMediaItems,
    tagMediaItems,
    filteredTagMediaItems,
    assetCategoryMediaItems,
    filteredAssetCategoryMediaItems,
    validAssetCategories,
    searchPerformed,
    currentMediaItemId,
  };
}

/**
 * Filter tags based on current filters and settings
 * @param {Object} payload - Current state payload
 * @returns {Object} Updated payload with filtered tags
 */
export function getVisualBuilderFilteredTags(payload) {
  const {
    primaryFilter,
    typeFilterId,
    contentTypeTags = {},
    validTags = {},
    hiddenTags = {},
    searchPerformed,
    tagsOrdered = {},
    tagNames = {},
    filteredTagMediaItems = {},
    filteredAssetCategoryMediaItems = {},
    assetCategoriesOrdered = {},
    contentTypeAssetCategories,
    assetCategoryNames = {},
  } = payload;

  const isTabsStatusPublic = isPublicPage(payload.tabStatus);
  const isTabsStatusCustomize = payload.tabStatus === TAB_STATUSES.CUSTOMIZE;

  // Process tags
  payload.tags = payload.tags.map((tag) => {
    tag.hidden = isTabsStatusCustomize || isTabsStatusPublic ? !!hiddenTags[tag.id] : !!hiddenTags[`media/${tag.id}`];
    tag.order = tagsOrdered[tag.id];
    tag.name = tagNames[tag.id] || tag.name;
    return tag;
  });

  // Process asset categories
  payload.assetCategories =
    payload.assetCategories?.map((category) => {
      category.order = assetCategoriesOrdered[category.id];
      category.name = assetCategoryNames[category.id] || category.name;
      return category;
    }) || [];

  // Filter asset categories
  payload.filteredAssetCategories = [...payload.assetCategories].filter((category) => {
    // Always show all asset categories when primaryFilter is TAG, even if no items currently match
    if (payload.primaryFilter === EXPERIENCE_PRIMARY_FILTER.TAG) {
      return true;
    }
    const facmiLen = filteredAssetCategoryMediaItems?.[category.id]?.length;
    return facmiLen;
  });

  // Filter tags
  payload.filteredTags = [...payload.tags].filter((tag) => {
    const isHidden = isTabsStatusCustomize || isTabsStatusPublic ? !!hiddenTags[`media/${tag.id}`] : tag.hidden;
    return !isHidden && filteredTagMediaItems[tag.id] && filteredTagMediaItems[tag.id].length;
  });

  // Additional public page filtering
  if (isTabsStatusPublic) {
    payload.filteredTags = payload.filteredTags.filter((tag) => !tag.hidden);
  }

  // Search filtering
  if (searchPerformed) {
    payload.filteredTags = payload.filteredTags.filter((tag) => !!validTags[tag.id]);
  }

  // Type filter
  if (primaryFilter === EXPERIENCE_PRIMARY_FILTER.TYPE && typeFilterId) {
    payload.filteredTags = payload.filteredTags.filter((tag) => contentTypeTags[typeFilterId].includes(tag.id));

    payload.filteredAssetCategories =
      payload.filteredAssetCategories?.filter((category) =>
        contentTypeAssetCategories[typeFilterId].includes(category.id),
      ) || [];
  }

  // Handle tag toggling
  if (payload.filteredTags.length && payload.shouldToggleTagItem && !isTabsStatusPublic) {
    if (payload.currentMediaItemId) {
      const { tags = [] } = payload.mediaItems.find(({ id }) => id === payload.currentMediaItemId) ?? {};

      tags.forEach((tagId) => {
        if (payload.filteredTags.find(({ id }) => id === tagId)) {
          payload.tagsToggled = { ...payload.tagsToggled, [tagId]: true };
        }
      });
    } else {
      payload.tagsToggled = { ...payload.tagsToggled, [payload.filteredTags[0].id]: true };
    }
  }

  if (payload.shouldToggleTagItem) {
    payload.shouldToggleTagItem = false;
  }

  return payload;
}

/**
 * Filter content types based on current filters and settings
 * @param {Object} payload - Current state payload
 * @returns {Object} Updated payload with filtered content types
 */
export function getVisualBuilderFilteredContentTypes(payload) {
  const { validContentTypes = {}, searchPerformed, hiddenContentTypes, contentTypesOrdered } = payload;

  // Process content types
  payload.contentTypes = payload.contentTypes.map((contentType) => {
    contentType.hidden = !!hiddenContentTypes[contentType.id];
    contentType.order = contentTypesOrdered[contentType.id];
    return contentType;
  });

  // Get visible content types
  payload.visibleContentTypes = payload.contentTypes.filter((contentType) => !contentType.hidden);

  // Initialize filtered content types
  payload.filteredContentTypes = [...payload.contentTypes];

  // Handle public page filtering
  if (isPublicPage(payload.tabStatus)) {
    payload.filteredContentTypes = payload.filteredContentTypes.filter(
      (contentType) => !payload.hiddenContentTypes[contentType.id],
    );
  }

  // Search filtering
  if (searchPerformed) {
    payload.filteredContentTypes = payload.filteredContentTypes.filter(
      (contentType) => !!validContentTypes[contentType.id],
    );
  }

  return payload;
}

/**
 * Sort content types by their order
 * @param {Object} payload - Current state payload
 * @returns {Object} Updated payload with sorted content types
 */
export function getSortedContentTypes(payload) {
  const { contentTypes = [], contentTypesOrdered = {} } = payload;

  const sortedContentTypes = contentTypes.sort((a, b) => contentTypesOrdered[a.id] - contentTypesOrdered[b.id]);

  return {
    ...payload,
    contentTypes: sortedContentTypes,
  };
}

/**
 * Sort tags and asset categories by their order
 * @param {Object} payload - Current state payload
 * @returns {Object} Updated payload with sorted tags and categories
 */
export function getSortedTags(payload) {
  const { tags = [], tagsOrdered = {}, assetCategories = [], assetCategoriesOrdered = {} } = payload;

  const sortedTags = tags.sort((a, b) => tagsOrdered[a.id] - tagsOrdered[b.id]);

  const sortedAssetCategories = assetCategories.sort(
    (a, b) => assetCategoriesOrdered[a.id] - assetCategoriesOrdered[b.id],
  );

  return {
    ...payload,
    tags: sortedTags,
    assetCategories: sortedAssetCategories,
  };
}

/**
 * Sort media items by their order
 * @param {Object} payload - Current state payload
 * @returns {Object} Updated payload with sorted media items
 */
export function getSortedMediaItems(payload) {
  payload.mediaItemsOrdered = {};

  // Calculate final order values
  Object.keys(payload.mediaItemsOrdered).forEach((key) => {
    payload.mediaItemsOrdered[key] = Math.min(...payload.mediaItemsOrdered[key]);
  });

  // Sort based on redesign flag
  const { typeFilterId, assetCategoryFilterId } = payload;

  if (typeFilterId && assetCategoryFilterId) {
    payload.mediaItems = payload.mediaItems.sort((a, b) => {
      const a1 = payload.experienceAssets?.find(
        (x) => x.contentTypeId === typeFilterId && x.assetCategoryId === assetCategoryFilterId && x.assetId === a.id,
      );
      const b1 = payload.experienceAssets?.find(
        (x) => x.contentTypeId === typeFilterId && x.assetCategoryId === assetCategoryFilterId && x.assetId === b.id,
      );
      return (a1?.order || 0) - (b1?.order || 0);
    });
  } else {
    payload.mediaItems = Array.isArray(payload.mediaItems) ? payload.mediaItems.sort((a, b) => a.id - b.id) : [];
  }

  return payload;
}

/**
 * Get drawer options based on current state and settings
 * @param {Object} params - Parameters for drawer options
 * @returns {Object} Drawer options configuration
 */
export function getDrawerOptions(params) {
  const { tabStatus, mediaItems, scope } = params;

  const values = { leftDrawerAutoHide: false, rightDrawerAutoHide: false };

  if (scope === SHARE_SCOPE.MEDIA_ITEM) {
    return {
      leftDrawerAutoHide: true,
      rightDrawerAutoHide: true,
    };
  }

  const isPublic = isPublicPage(tabStatus);
  const hasSingleMediaItem = mediaItems.length <= 1;

  if (!isPublic) {
    return values;
  }

  return {
    leftDrawerAutoHide: hasSingleMediaItem,
    rightDrawerAutoHide: true,
  };
}

/**
 * Check if current tab status is a public page
 * @param {string | undefined} tabStatus - Current tab status
 * @returns {boolean} Whether the current page is public
 */
export const isPublicPage = (tabStatus) =>
  tabStatus === TAB_STATUSES.PUBLIC ||
  tabStatus === TAB_STATUSES.PUBLIC_MEDIA_SHARE ||
  tabStatus === TAB_STATUSES.PUBLIC_SINGLE_SHARE;

/**
 * Returns the allowed tiers for media item collection based on the current context.
 */
export const getAllowedTiersForMediaItemSelection = (isOrganizationsSection, isHomebase) => {
  if (isOrganizationsSection) {
    // all tiers allowed in organizations section
    return [];
  }
  if (isHomebase) {
    // only pro allowed in presentation tab
    return [PROPERTY_TIERS.PRO];
  }
  // only pro and standard allowed in other tabs
  return [PROPERTY_TIERS.PRO, PROPERTY_TIERS.STANDARD];
};
